/**
 * Fire-and-forget trigger for the AI fetch flow from any surface (list card,
 * modal, etc.). The full interactive flow (2FA prompts, task polling,
 * breakdown display) still lives in the Insurance Breakdown modal / Step 2
 * view; this helper just kicks off the request so the user can monitor
 * results there.
 */

import { controller as treatmentsController } from "../controller";

/**
 * Start an AI fetch for the given policy.
 *
 * @param {number|string} policyID  InsurancePolicy.id to fetch for.
 * @param {object}  opts
 * @param {boolean} opts.isForBreakdown       Whether this is a breakdown fetch.
 * @param {number}  opts.insuranceBreakdownId Optional existing breakdown id.
 * @returns {Promise<{ok: boolean, data: any, error?: string}>}
 */
export async function triggerAIFetch(policyID, opts = {}) {
  if (!policyID) {
    return { ok: false, data: null, error: "Missing policy id" };
  }
  try {
    const res = await treatmentsController.patientAction(policyID, opts);
    const ok = res && typeof res.status === "number" && res.status < 300;
    if (!ok) {
      const detail =
        (res && res.json && (res.json.error || res.json.detail)) ||
        "The AI fetch couldn't be started.";
      return { ok: false, data: res && res.json, error: detail };
    }
    return { ok: true, data: res.json };
  } catch (err) {
    return {
      ok: false,
      data: null,
      error: (err && err.message) || "The AI fetch couldn't be started.",
    };
  }
}

/**
 * Start an AI estimate (predetermination) fetch for the given policy, optionally
 * fanned out across the chosen provider accounts (one scrape each). Mirrors
 * triggerAIFetch but hits the predetermination endpoint. Fire-and-forget: the
 * interactive 2FA / polling / results surface in the Insurance Estimate modal
 * (and the global scrape-job 2FA modal), same as the breakdown "Instant Check".
 *
 * @param {number|string} policyID     InsurancePolicy.id to fetch for.
 * @param {Array<number|string>} providerIds  Provider ids to fetch under ([] → office default).
 * @returns {Promise<{ok: boolean, data: any, error?: string}>}
 */
export async function triggerEstimateFetch(policyID, providerIds = []) {
  if (!policyID) {
    return { ok: false, data: null, error: "Missing policy id" };
  }
  try {
    const res = await treatmentsController.startPredeterminationAgent(policyID, providerIds || []);
    const ok = res && typeof res.status === "number" && res.status < 300;
    if (!ok) {
      const detail =
        (res && res.json && (res.json.error || res.json.detail)) ||
        "The estimate fetch couldn't be started.";
      return { ok: false, data: res && res.json, error: detail };
    }
    return { ok: true, data: res.json };
  } catch (err) {
    return {
      ok: false,
      data: null,
      error: (err && err.message) || "The estimate fetch couldn't be started.",
    };
  }
}
